import {
  Card,
  Flex,
  Form,
  Typography,
  Input,
  Button,
  ConfigProvider,
  Tooltip,
} from 'antd';
import PropTypes from 'prop-types';
import { HYPHEN, TRIP_VIDEO_STATUS } from '../../../utilities/constants';
import Icon from '@ant-design/icons';
import { ReactComponent as BookmarkBlue } from '../../../assets/icons/bookmark-blue.svg';
import { ReactComponent as BookmarkGrey } from '../../../assets/icons/bookmark-grey.svg';
import { API } from '../../../services';
import {
  formatTimeDifferenceInHours,
  getFormattedTimeFromTimestamp,
  sanitizeInput,
  getErrorMessage,
} from '../../../utilities/commonFunctions';
import { useContext, useEffect, useState } from 'react';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import ActionConfirmationModal from './ActionConfirmationModal';
import EmptyCard from '../../../components/EmptyCard/EmptyCard';

const { Text } = Typography;
const { TextArea } = Input;

const PastTripsActionCard = ({
  selectedTrip,
  selectedFragment,
  setSelectedFragmentDetails,
}) => {
  const [form] = Form.useForm();
  const [actionLoading, setActionLoading] = useState(false);
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [isActionConfirmationModalOpen, setIsActionConfirmationModalOpen] =
    useState(false);

  useEffect(() => {
    form.setFieldsValue({
      comment: selectedFragment?.comment || '',
    });
  }, [selectedFragment, form]);

  const showCharacterCount = ({ count, maxLength }) => (
    <Text>
      <small>
        {count}/{maxLength} characters
      </small>
    </Text>
  );

  const onFinish = async values => {
    try {
      setActionLoading(true);
      const payload = {
        trip_id: selectedTrip?.trip_id,
        fragment_uuid: selectedFragment?.fragment_uuid,
        comment: sanitizeInput(values?.comment),
      };
      const { status, data } = await API.postPastTripsVideoAction(payload);
      if (status === 200) {
        addErrorMessage(data?.message, 'success');
        setSelectedFragmentDetails(prev => ({
          ...prev,
          comment: sanitizeInput(values?.comment),
        }));
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to submit action! Please try again after sometime.'
        )
      );
    } finally {
      setActionLoading(false);
    }
  };

  const bookmarkFragment = async () => {
    try {
      const payload = {
        trip_id: selectedTrip?.trip_id,
        fragment_list: [
          {
            fragment_uuid: selectedFragment?.fragment_uuid,
            is_bookmarked: !selectedFragment?.is_bookmarked,
          },
        ],
      };
      const { status } = await API.postBookmarkTripFragment(payload);
      if (status === 200) {
        addErrorMessage(
          selectedFragment?.is_bookmarked === true
            ? 'Bookmark has been removed from the selected clip.'
            : 'The clip has been bookmarked for your reference. You can review clip under Retrieved Footages',
          'success'
        );
        setSelectedFragmentDetails(prev => ({
          ...prev,
          is_bookmarked: !selectedFragment?.is_bookmarked,
        }));
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to bookmark fragment! Please try again after sometime.'
        )
      );
    } finally {
      setIsActionConfirmationModalOpen(false);
    }
  };

  const handleFragmentBookmark = () => {
    if (selectedFragment?.is_bookmarked) {
      setIsActionConfirmationModalOpen(true);
    } else {
      bookmarkFragment();
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            boxShadowTertiary: 'none',
          },
        },
      }}
    >
      <Card variant="borderless" className="full-height past-trip-action-card">
        <Flex vertical gap={41} className="full-height">
          <Flex vertical gap={20}>
            <Flex gap={16} align="center" justify="space-between">
              <Text
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  fontFamily: "'BoschSansBold', sans-serif",
                }}
              >
                {selectedTrip?.registration || HYPHEN}
              </Text>
              {selectedTrip?.sticker_id && (
                <Text
                  style={{
                    backgroundColor: '#006EAD',
                  }}
                  className="dms-sticker-id"
                >
                  {selectedTrip?.sticker_id}
                </Text>
              )}
            </Flex>

            <Flex vertical gap={16}>
              <Text>Driver Name: {selectedTrip?.drivername || HYPHEN}</Text>
              <Text>
                Phone number:{' '}
                {selectedTrip?.driver_phone_numbers?.join(', ') || HYPHEN}
              </Text>
              <Text>
                Trip duration:{' '}
                {formatTimeDifferenceInHours(
                  selectedTrip?.trip_start_time,
                  selectedTrip?.trip_end_time
                ) || HYPHEN}
              </Text>
            </Flex>
          </Flex>
          <Flex vertical gap={24} className="full-height">
            <Flex justify="space-between">
              <Text
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  fontFamily: "'BoschSansBold', sans-serif",
                }}
              >
                Clip details
              </Text>
              {selectedFragment?.vod_status === TRIP_VIDEO_STATUS.AVAILABLE && (
                <Tooltip
                  title={
                    selectedFragment?.is_bookmarked
                      ? 'Remove bookmark'
                      : 'Bookmark this segment'
                  }
                >
                  <Flex
                    gap={8}
                    align="center"
                    style={{ cursor: 'pointer' }}
                    onClick={handleFragmentBookmark}
                  >
                    <Icon
                      style={{ fontSize: '1.5rem' }}
                      component={
                        selectedFragment?.is_bookmarked
                          ? BookmarkGrey
                          : BookmarkBlue
                      }
                    />
                    <Text
                      style={{
                        color: `${selectedFragment?.is_bookmarked ? '#8A9097' : '#0096E8'}`,
                        fontSize: '1rem',
                      }}
                    >
                      Bookmark clip
                    </Text>
                  </Flex>
                </Tooltip>
              )}
            </Flex>
            {selectedFragment === null ? (
              <EmptyCard
                description={
                  <Flex vertical gap={20}>
                    <Text strong>No clip selected</Text>
                    <Text style={{ color: '#979EA4' }}>
                      Please select a clip to view details
                    </Text>
                  </Flex>
                }
              />
            ) : (
              <>
                <Flex vertical gap={16}>
                  <Flex gap={16} align="center">
                    <Text>
                      Clip ID: {selectedFragment?.fragment_sequence || HYPHEN}
                    </Text>
                  </Flex>
                  <Text>
                    Start Time:{' '}
                    {getFormattedTimeFromTimestamp(
                      selectedFragment?.fragment_timestamp
                    ) || HYPHEN}
                  </Text>
                </Flex>
                {selectedFragment?.vod_status ===
                  TRIP_VIDEO_STATUS.AVAILABLE && (
                  <>
                    <Form
                      form={form}
                      onFinish={onFinish}
                      layout="vertical"
                      className="full-height broadcast-form"
                      scrollToFirstError
                      requiredMark={false}
                      disabled={actionLoading}
                    >
                      <Form.Item
                        name="comment"
                        className="comment-form-item-modal"
                        label={
                          <Text
                            strong
                            style={{ marginTop: '20px', fontSize: 20 }}
                          >
                            Comments
                          </Text>
                        }
                        initialValue={selectedFragment?.comment || ''}
                      >
                        <TextArea
                          autoSize
                          placeholder="Type your message here&#10;e.g: alerted the driver"
                          rows={4}
                          maxLength={500}
                          count={{
                            max: 500,
                            show: showCharacterCount,
                          }}
                          readOnly={selectedFragment?.comment !== ''}
                        />
                      </Form.Item>
                    </Form>
                    {selectedFragment?.comment === '' && (
                      <Flex gap={24} justify="end">
                        <Button
                          disabled={actionLoading}
                          onClick={() => form.resetFields()}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => form.submit()}
                          loading={actionLoading}
                        >
                          Save
                        </Button>
                      </Flex>
                    )}
                  </>
                )}
              </>
            )}
          </Flex>
        </Flex>
        <ActionConfirmationModal
          isModalOpen={isActionConfirmationModalOpen}
          setIsModalOpen={setIsActionConfirmationModalOpen}
          actionType="removeBookmark"
          selectedData={selectedFragment}
          handleAction={bookmarkFragment}
        />
      </Card>
    </ConfigProvider>
  );
};

PastTripsActionCard.propTypes = {
  selectedTrip: PropTypes.object,
  selectedFragment: PropTypes.object,
  setSelectedFragmentDetails: PropTypes.func.isRequired,
};

export default PastTripsActionCard;
